import React from "react";
import PropTypes from "prop-types";

// Project-specific Material UI wrappers
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Card, CardContent, Grid, Icon } from "@mui/material";
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";

function SummaryItem({ label, value, columns }) {
  const gridSize = columns === 3 ? { xs: 4, sm: 4 } : { xs: 6, sm: 6 };

  return (
    <Grid size={gridSize}>
      <MDBox display="flex" flexDirection="column" alignItems="center" px={0.5}>
        <MDTypography
          variant="button"
          color="text"
          textAlign="center"
          lineHeight={1.2}
          sx={{ minHeight: 34, display: "flex", alignItems: "center" }}
        >
          {label}
        </MDTypography>
        <MDTypography variant="h4" fontWeight="bold" lineHeight={1} mt={0.5}>
          {value}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  columns: PropTypes.number,
};

SummaryItem.defaultProps = {
  columns: 3,
};

function EventRow({ title, time }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1.0}>
      <MDBox>
        <MDTypography variant="body1" fontWeight="medium">
          {title}
        </MDTypography>
      </MDBox>
      {time && (
        <MDTypography variant="body1" color="text">
          {time}
        </MDTypography>
      )}
    </MDBox>
  );
}

EventRow.defaultProps = {
  time: 0,
};

EventRow.propTypes = {
  title: PropTypes.string.isRequired,
  time: PropTypes.string,
};

export default function DashboardCard({
  title,
  icon,
  summaryItems,
  range,
  onRangeChange,
  showFilter,
  showEvents,
  events,
  maxVisibleEvents = 3,
  onClick,
}) {
  const [controller] = useMaterialUIController();
  const { t } = useTranslation();

  const { darkMode } = controller;

  const hasMore = events.length > maxVisibleEvents;
  const visibleEvents = hasMore ? events.slice(0, maxVisibleEvents) : events;
  const summaryColumns = summaryItems.length >= 3 ? 3 : 2;

  // sidenav type buttons styles
  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  return (
    <Card className="rounded-2xl shadow-md" style={{ height: "100%", justifyContent: "center" }}>
      <CardContent onClick={onClick} sx={{ cursor: onClick ? "pointer" : "default" }}>
        {/* Header */}
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={2} onClick={onClick}>
          <MDBox display="flex" alignItems="center" p={2}>
            {icon && (
              <MDBox
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                width={60}
                height={60}
                className="rounded-2xl shadow"
              >
                <Icon fontSize="xl" color="info">
                  {icon}
                </Icon>
              </MDBox>
            )}
            <MDTypography variant="h4" fontWeight="bold">
              {title}
            </MDTypography>
          </MDBox>

          {/* Summary */}
          {summaryItems.length > 0 && (
            <Grid container mb={2} spacing={summaryColumns === 3 ? 3 : 2} justifyContent="center">
              {summaryItems.map((item) => (
                <SummaryItem
                  key={item.key ?? item.label}
                  label={item.label}
                  value={item.value}
                  columns={summaryColumns}
                />
              ))}
            </Grid>
          )}

          {/* Range selector */}
          {showFilter && (
            <MDBox display="flex" gap={1}>
              <MDButton
                size="small"
                variant="gradient"
                fullWidth
                sx={range === "today" ? sidenavTypeActiveButtonStyles : sidenavTypeButtonsStyles}
                onClick={() => onRangeChange && onRangeChange("today")}
              >
                {t("todayLabel")}
              </MDButton>
              <MDButton
                size="small"
                variant="gradient"
                fullWidth
                sx={range === "week" ? sidenavTypeActiveButtonStyles : sidenavTypeButtonsStyles}
                onClick={() => onRangeChange && onRangeChange("week")}
              >
                {t("oneWeek")}
              </MDButton>
              <MDButton
                size="small"
                variant="gradient"
                fullWidth
                sx={range === "month" ? sidenavTypeActiveButtonStyles : sidenavTypeButtonsStyles}
                onClick={() => onRangeChange && onRangeChange("month")}
              >
                {t("oneMonth")}
              </MDButton>
            </MDBox>
          )}
        </MDBox>

        {/* Divider */}
        {/* <MDBox sx={{ height: 1, width: "100%", backgroundColor: "divider", my: 1 }} /> */}

        {/* Events List */}
        {showEvents && (
          <MDBox display="flex" flexDirection="column" height="">
            {visibleEvents.length === 0 ? (
              <MDTypography variant="button" color="text">
                {t("noEvents")}
              </MDTypography>
            ) : (
              visibleEvents.map((e, idx) => (
                <EventRow key={e.id ?? idx} title={e.title} time={e.time} />
              ))
            )}

            {hasMore ? (
              <MDBox mt={0.5}>
                <MDTypography variant="button" color="text">
                  {t("more")}...
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox mt={0.5}>
                <MDTypography variant="button" color="text">
                  {t("noMoreElements")}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        )}
      </CardContent>
    </Card>
  );
}

DashboardCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  summaryItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  range: PropTypes.oneOf(["today", "week", "month"]),
  onRangeChange: PropTypes.func,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      time: PropTypes.string,
    })
  ),
  maxVisibleEvents: PropTypes.number,
  onClick: PropTypes.func,
  showFilter: PropTypes.bool,
  showEvents: PropTypes.bool,
};

DashboardCard.defaultProps = {
  icon: "",
  summaryItems: [],
  range: "today",
  onRangeChange: null,
  events: [],
  maxVisibleEvents: 3,
  onClick: null,
  showFilter: true,
  showEvents: false,
};
